// src/services/offlineSync.js
//
// The offline queue's replay engine (this feature's own plan doc, "Real
// Offline Mode for the Cashier/Checkout Screen") — drains offlineDb's
// "queue" store in order once connectivity returns, called by
// OfflineContext.js's syncNow() (itself triggered by the ping probe coming
// back positive, the browser's own "online" event, or a manual retry).
//
// Replay order is dependency-safe BY CONSTRUCTION, not by a separate
// dependency graph: IndexedDB's autoIncrement queue ids are already
// insertion order, and Checkout.js can only ever queue a sale or a
// close_shift once its shift is already set — an open_shift item for that
// same shift is therefore always queued strictly earlier. The one thing
// that still needs tracking is a "close_shift" item whose own shift was
// ALSO opened offline: it can't be sent until its open_shift sibling has
// actually replayed and produced a real, server-assigned shift id (see
// shiftIdMap below) — a fabricated "offline-<uuid>" id means nothing to the
// backend's own /shifts/:id/close route.
import { openShift, closeShift, createOrder } from "./api";
import {
  getQueueItems,
  updateQueueItem,
  deleteQueueItem,
  markSyncComplete,
  setCached,
} from "./offlineDb";

// isNetworkError distinguishes "the request never reached the server"
// (worth retrying later, unchanged) from "the server answered with a real
// error" (a genuine failure — see api.js's request(), which only ever
// attaches .data once an actual HTTP response came back; its absence here
// means fetch() itself threw, e.g. the browser has no connectivity at all).
function isNetworkError(err) {
  return !err?.data;
}

export async function drainQueue() {
  const token = localStorage.getItem("token");
  if (!token) return { hasFailures: false };

  const items = await getQueueItems();
  // open_shift's own clientRequestId -> the real, server-assigned shift id
  // — resolved as this pass replays each open_shift item, and consulted by
  // any close_shift item queued for a shift that was itself opened offline.
  const shiftIdMap = {};
  let hasFailures = false;

  for (const item of items) {
    // Another drainQueue() call (a second tab, or an overlapping trigger)
    // already has this item in flight — leave it alone.
    if (item.status === "syncing") continue;

    if (item.type === "close_shift" && !item.shiftServerId && item.dependsOnShiftClientRequestId && !shiftIdMap[item.dependsOnShiftClientRequestId]) {
      // The open_shift item this close depends on hasn't resolved YET this
      // pass. If it's sitting right there in the queue still pending/
      // syncing, just wait for a later pass. If it has already given up
      // (status "failed" — a genuine, non-transient error, not a dropped
      // connection), this close can never succeed either: surface it as a
      // cascading failure instead of leaving it silently stuck forever.
      const dep = items.find((i) => i.type === "open_shift" && i.clientRequestId === item.dependsOnShiftClientRequestId);
      if (dep && dep.status === "failed") {
        await updateQueueItem(item.id, {
          status: "failed",
          error: "Can't close this shift — the offline shift-open it depends on failed to sync",
        });
        hasFailures = true;
      }
      continue;
    }

    await updateQueueItem(item.id, { status: "syncing" });
    try {
      if (item.type === "open_shift") {
        const real = await openShift(token, item.payload);
        shiftIdMap[item.clientRequestId] = real.id;
        setCached("currentShift", real).catch(() => {});
        await deleteQueueItem(item.id);
      } else if (item.type === "sale") {
        // item.payload already carries client_request_id/is_offline_sync —
        // CreateOrderHandler's own idempotent-replay lookup (see
        // order_handler.go) makes this safe even if drainQueue somehow ran
        // this same item twice.
        await createOrder(token, item.payload);
        await deleteQueueItem(item.id);
      } else if (item.type === "close_shift") {
        const shiftId = item.shiftServerId || shiftIdMap[item.dependsOnShiftClientRequestId];
        if (!shiftId) {
          // Guarded above — this is a defensive backstop, not an expected
          // path: never send a close request with no target to close.
          throw { data: { error: "No resolved shift id for this close" } };
        }
        await closeShift(token, shiftId, item.payload);
        setCached("currentShift", null).catch(() => {});
        await deleteQueueItem(item.id);
      }
    } catch (err) {
      if (isNetworkError(err)) {
        // Connectivity dropped again mid-drain — put it back to pending
        // and stop this pass entirely; the next online/interval trigger
        // retries from the top rather than racing ahead with items that
        // depend on this one.
        await updateQueueItem(item.id, { status: "pending" });
        return { hasFailures };
      }
      // A genuine, non-transient failure (a real validation error, an
      // actual conflict) — never auto-retried forever; left for a human to
      // review (see OfflineQueueReview.js) instead.
      await updateQueueItem(item.id, {
        status: "failed",
        error: err.data?.error || err.message || "Sync failed",
      });
      hasFailures = true;
    }
  }

  const remaining = await getQueueItems();
  if (remaining.every((i) => i.status !== "pending" && i.status !== "syncing")) {
    // Nothing left that could still succeed on its own this offline
    // stretch — either everything drained, or whatever's left needs a
    // human (status "failed"), not another automatic pass. Either way the
    // till is back online now, so the offline-duration cap resets for the
    // NEXT stretch rather than carrying this one forward.
    await markSyncComplete();
  }

  return { hasFailures };
}
